import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Loader2, Mail, Lock, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const Login = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t('auth.fillAllFields'));
      return;
    }
    
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('auth.welcomeBackToast'));
      navigate("/");
    } catch (error) {
      console.error('Login error:', error?.message, error?.response?.status, error?.response?.data);
      const detail = error.response?.data?.detail;
      toast.error(detail || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth-callback.html';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        {/* Language Toggle */}
        <div className="flex justify-end items-center gap-2 mb-2">
          <span className="text-xs sm:text-sm text-slate-500">Language</span>
          <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs sm:text-sm font-medium" data-testid="lang-toggle">
            <button
              onClick={() => i18n.changeLanguage('de')}
              className={`px-2.5 py-1.5 sm:px-3 sm:py-1.5 transition-colors ${i18n.language?.startsWith('de') ? 'bg-[#0F766E] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >DE</button>
            <button
              onClick={() => i18n.changeLanguage('en')}
              className={`px-2.5 py-1.5 sm:px-3 sm:py-1.5 transition-colors ${i18n.language?.startsWith('en') ? 'bg-[#0F766E] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >EN</button>
            <button
              onClick={() => i18n.changeLanguage('fr')}
              className={`px-2.5 py-1.5 sm:px-3 sm:py-1.5 transition-colors ${i18n.language?.startsWith('fr') ? 'bg-[#0F766E] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >FR</button>
          </div>
        </div>
        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="text-center pb-2 px-4 sm:px-6 pt-4 sm:pt-6">
            <div className="mx-auto mb-3 sm:mb-4 w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-white" strokeWidth={1.5} />
            </div>
            <CardTitle className="text-xl sm:text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>
              {t('auth.welcomeBack')}
            </CardTitle>
            <CardDescription className="text-sm">{t('auth.signInToTracker')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 px-4 sm:px-6 pb-4 sm:pb-6">
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="email" className="text-sm">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-10 sm:h-12 text-sm sm:text-base"
                    data-testid="login-email"
                  />
                </div>
              </div>
              <div className="space-y-1.5 sm:space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm">{t('auth.password')}</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-xs sm:text-sm text-teal-600 hover:text-teal-700"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-10 sm:h-12 text-sm sm:text-base"
                    data-testid="login-password"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full h-10 sm:h-12 bg-[#0F766E] hover:bg-[#115E59] text-sm sm:text-base"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                ) : (
                  t('auth.login')
                )}
              </Button>
            </form>

            <div className="relative my-4 sm:my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs sm:text-sm">
                <span className="bg-white px-3 sm:px-4 text-slate-500">{t('auth.continueWith')}</span>
              </div>
            </div>

            <Button 
              type="button"
              variant="outline"
              className="w-full h-10 sm:h-12 border-slate-200 text-sm sm:text-base"
              onClick={handleGoogleLogin}
              data-testid="google-login"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {t('auth.signInWithGoogle')}
            </Button>

            <p className="text-center text-xs sm:text-sm text-slate-600 mt-4 sm:mt-6">
              {t('auth.noAccount')}{" "}
              <Link to="/signup" className="text-teal-600 hover:text-teal-700 font-medium">
                {t('auth.signup')}
              </Link>
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Login;
